import groupBy from 'lodash/groupBy'
import head from 'lodash/head'
import snakeCase from 'lodash/snakeCase'
import split from 'lodash/split'
import values from 'lodash/values'
import getConfig from 'next/config'

import { Options, Result } from '@Globals/Algolia/Types'

const sortingSuffix = (sorting: Options['sorting']) => {
  switch (sorting) {
    case 'default':
      return ''
    default:
      return `_${sorting}`
  }
}

const generateIndex = ({ locale, sorting }: Options) => {
  const { publicRuntimeConfig } = getConfig()
  return `products_${snakeCase(locale)}${publicRuntimeConfig.algoliaIndexSuffix}${sortingSuffix(sorting)}`
}

const generateParams = ({ facetFilters, facets, filters, optionalFilters }: Options) => {
  const searchParams = new URLSearchParams()

  if (facets.length > 0) {
    searchParams.set('facets', JSON.stringify(facets))
  }

  if (facetFilters.length > 0) {
    searchParams.set('facetFilters', JSON.stringify(
      values(
        groupBy(
          facetFilters,
          item => head(split(item, ':')),
        ),
      ),
    ))
  }

  if (filters) {
    searchParams.set('filters', filters)
  }

  if (optionalFilters.length > 0) {
    searchParams.set('optionalFilters', JSON.stringify(optionalFilters))
  }

  return searchParams.toString()
}

const generateURL = () => {
  const { publicRuntimeConfig } = getConfig()
  const url = new URL(`https://${publicRuntimeConfig.algoliaAppID.toLowerCase()}-1.algolia.net/1/indexes/*/queries`)
  url.searchParams.set('x-algolia-application-id', publicRuntimeConfig.algoliaAppID)
  url.searchParams.set('x-algolia-api-key', publicRuntimeConfig.algoliaAPIKey)
  return url.toString()
}

// TODO: Also handle query params by sending two requests
export const algoliaFetcher = async (options: Options): Promise<Result> => {
  const url = generateURL()

  const fetchedData = await fetch(url, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json; charset=UTF-8',
    }),
    body: JSON.stringify({
      requests: [
        {
          indexName: generateIndex(options),
          params: generateParams(options),
        },
      ],
    }),
  })

  if (fetchedData.status !== 200) {
    throw new Error('Failed to fetch from Algolia')
  }

  try {
    const data = await fetchedData.json()

    if (data?.results && data.results.length > 0) {
      return {
        facets: data?.results[0]?.facets || {},
        facetsStats: data?.results[0]?.facets_stats || {},
        hits: data?.results[0]?.hits || [],
      }
    }

    return {
      facets: {},
      facetsStats: {},
      hits: [],
    }
  } catch (e) {
    throw new Error('Something went wrong trying to get data from Algolia')
  }
}
